import { Link } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Instagram, 
  Linkedin, 
  ArrowRight 
} from 'lucide-react';

// Custom TikTok Icon since lucide doesn't have it
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Print on Demand', href: '/services#print-on-demand' },
    { name: 'Dropshipping Fulfillment', href: '/services#dropshipping' },
    { name: 'Product Sourcing', href: '/services#product-sourcing' },
    { name: 'Global Warehousing', href: '/services#global-warehousing' },
    { name: 'Custom Branding', href: '/services#custom-branding' },
    { name: 'TikTok Shop Solutions', href: '/services#tiktok-shop' },
  ];

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/contact' },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/BlackMesaMercantile' },
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/BlackMesaMercantile' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/BlackMesaMercantile' },
    { name: 'TikTok', icon: TikTokIcon, href: 'https://tiktok.com/@BlackMesaMercantile' },
  ];

  return (
    <footer data-testid="footer" className="bg-gray-900 text-gray-300 relative overflow-hidden">
      {/* Top CTA Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-white mb-2">
                Ready to Scale Your Business?
              </h3>
              <p className="text-gray-400">
                Partner with us for seamless fulfillment solutions.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="footer-cta-button"
              className="flex items-center gap-2 bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group whitespace-nowrap"
            >
              Get Started
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Chivo']">BM</span>
              </div>
              <div>
                <span className="font-['Chivo'] font-bold text-white text-lg block">Black Mesa</span>
                <span className="text-xs text-gray-500 -mt-0.5 block">Mercantile LLC</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              Your Trusted Path to Fulfillment Excellence. We provide comprehensive ecommerce fulfillment solutions worldwide.
            </p>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-['Chivo'] font-bold text-white text-lg mb-5">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    data-testid={`footer-link-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 hover:text-green-500 text-sm transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-gray-600 rounded-full group-hover:bg-green-500 transition-colors" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Chivo'] font-bold text-white text-lg mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 hover:text-green-500 text-sm transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-gray-600 rounded-full group-hover:bg-green-500 transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Chivo'] font-bold text-white text-lg mb-5">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:support@Blackmesamer.com"
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-gray-400 hover:text-green-500 text-sm transition-colors duration-300"
                >
                  <Mail size={18} className="mt-0.5 flex-shrink-0" />
                  <span>support@Blackmesamer.com</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+15745667535"
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-gray-400 hover:text-green-500 text-sm transition-colors duration-300"
                >
                  <Phone size={18} className="mt-0.5 flex-shrink-0" />
                  <span>+1 574 566 7535</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-400 text-sm">
                  <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                  <span>1109 Cincinnati St,<br />Lafayette, IN 47904</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <p>© {currentYear} Black Mesa Mercantile LLC. All rights reserved.</p>
            <p>
              Website:{' '}
              <a
                href="https://Blackmesamer.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-green-500 transition-colors"
              >
                Blackmesamer.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
